import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useCountry } from "../contexts/CountryContext";
import { SEOHead } from "../components/SEOHead";
import { FRONT_DOOR_ALTERNATES } from "../data/languages";
import { faqJsonLd } from "../data/faq-data";

import { HeroLanding } from "../components/landing/HeroLanding";
import { UniversityMarquee } from "../components/landing/UniversityMarquee";
import { MoreThanAVisa } from "../components/landing/MoreThanAVisa";
import { ForUniversities } from "../components/landing/ForUniversities";
import { MoveSections } from "../components/landing/MoveSections";
import { TrustSection } from "../components/landing/TrustSection";
import { FlagRoll } from "../components/landing/FlagRoll";
import { Stories } from "../components/landing/Stories";
import { Buddies } from "../components/landing/Buddies";
import { NationalityGuides } from "../components/landing/NationalityGuides";
import { LandingFAQ } from "../components/landing/LandingFAQ";

/**
 * HomePage: the Migrada landing page, built on the September 2026 strategy.
 * Migrada is a promise, not a platform: a free personal plan, every step until
 * arrival, a community and honest numbers. No time promise (Noah, 14 Oct).
 *
 * Section order:
 *   Hero (plan request) → UniversityMarquee → MoreThanAVisa (three 3D cards) → ForUniversities → TrustSection → FlagRoll → MoveSections (#your-move, #people:
 *   two moving frames) → Stories (real people only) → Buddies (#buddies) →
 *   NationalityGuides → LandingFAQ (#faq, with a parents tab at #parents)
 *   (Navbar and Footer come from the public shell in App.tsx.)
 */
export default function HomePage() {
  const { isConfirmed, resetSelections } = useCountry();
  const location = useLocation();
  const isRootPath = location.pathname === "/";

  // Coming back to / (e.g. with the back button from /india) starts fresh.
  useEffect(() => {
    if (isRootPath && isConfirmed) {
      resetSelections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRootPath]);

  return (
    <>
      <SEOHead
        title="Austrian Student Visa: Every Step Until You Arrive"
        description="Your Austrian student visa, step by step: what to prepare, your documents checked, your deadlines and a community of students who made the move."
        canonical="https://migrada.eu"
        alternates={FRONT_DOOR_ALTERNATES}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Migrada",
            alternateName: "migrada.eu",
            url: "https://migrada.eu",
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Migrada",
            url: "https://migrada.eu",
            logo: "https://migrada.eu/icon-512.png",
            email: "noah-rodman@hotmail.com",
            description: "Free help for students from outside the EU who move to Austria to study: a personal plan, document checks, deadlines and a community.",
            founder: { "@type": "Person", name: "Noah Rodman", jobTitle: "Founder", address: { "@type": "PostalAddress", addressLocality: "Vienna", addressCountry: "AT" } },
            foundingLocation: { "@type": "Place", name: "Vienna, Austria" },
            areaServed: "Worldwide",
            knowsAbout: ["Austrian student residence permit", "Aufenthaltsbewilligung Student", "Studying in Austria", "Apostille and legalisation for Austria", "Proof of financial means for Austria"],
            sameAs: ["https://www.reddit.com/r/MoveToEurope/"],
            address: { "@type": "PostalAddress", streetAddress: "Donau-City-Straße 3", postalCode: "1220", addressLocality: "Wien", addressCountry: "AT" },
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Migrada",
            url: "https://migrada.eu",
            serviceType: "Student residence permit guidance for Austria",
            description:
              "Free help for students from outside the EU moving to Austria to study: a personal plan, a case with every step in order, checks of uploaded documents, deadline reminders and a community of students who made the move.",
            areaServed: "Worldwide",
            audience: { "@type": "Audience", audienceType: "Students from outside the EU and their parents" },
            offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "Free for students" },
            provider: { "@type": "Organization", name: "Migrada", url: "https://migrada.eu" },
          },
          faqJsonLd,
        ]}
      />

      <HeroLanding />
      <UniversityMarquee />
      <MoreThanAVisa />
      <ForUniversities />
      <TrustSection />
      <FlagRoll />
      <MoveSections />
      <Stories />
      <Buddies />
      <NationalityGuides />
      <LandingFAQ />
    </>
  );
}
